import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Wrench, ArrowLeft, ShoppingBag, Check, History, FileText, Lock, Rocket, Mail, Phone
} from "lucide-react";

const BuyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="px-6 py-4 flex items-center gap-4 max-w-6xl mx-auto border-b border-[#E2E8F0] bg-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/')}
          className="text-[#475569] hover:bg-[#F1F5F9]"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Wrench className="w-8 h-8 text-[#2563EB]" />
          <span className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
            QuickQrId
          </span>
        </div>
      </header>

      <div className="px-6 py-8 max-w-4xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-10 h-10 text-[#22C55E]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Manrope' }}>
            Get Machine QR Codes
          </h1>
          <p className="text-[#475569] text-lg">
            Order QR stickers for your machines and equipment
          </p>
        </div>

        {/* CTA Card */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm mb-8 max-w-2xl mx-auto">
          <CardContent className="p-8 md:p-12 text-center">
            <div className="w-16 h-16 bg-[#2563EB]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Rocket className="w-8 h-8 text-[#2563EB]" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Manrope' }}>
              Digitize Your Machine Maintenance Today
            </h2>
            <p className="text-[#475569] leading-relaxed text-lg mb-6">
              Get custom QR codes for your machines and access maintenance records instantly with a single scan.
            </p>
            
            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 border-t border-[#E2E8F0]">
              <a 
                href="mailto:quickqridinfo@gmail.com" 
                className="flex items-center gap-2 text-[#475569] hover:text-[#2563EB] transition-colors"
              >
                <Mail className="w-5 h-5 text-[#2563EB]" />
                <span>quickqridinfo@gmail.com</span>
              </a>
              <a 
                href="tel:+919209908344" 
                className="flex items-center gap-2 text-[#475569] hover:text-[#2563EB] transition-colors"
              >
                <Phone className="w-5 h-5 text-[#2563EB]" />
                <span>+91 9209908344</span>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* What's Included */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm max-w-2xl mx-auto mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-bold text-[#0F172A] mb-6 text-center" style={{ fontFamily: 'Manrope' }}>
              What You'll Get
            </h2>
            <div className="space-y-4">
              {[
                { icon: Wrench, text: "High-quality QR code stickers for your machines" },
                { icon: History, text: "Complete maintenance history tracking" },
                { icon: FileText, text: "Store operating instructions & safety info" },
                { icon: Lock, text: "PIN-protected editing access" },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#22C55E]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#22C55E]" />
                  </div>
                  <span className="text-[#0F172A]">{item.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Benefits */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm max-w-2xl mx-auto">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-bold text-[#0F172A] mb-6 text-center" style={{ fontFamily: 'Manrope' }}>
              Benefits
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "No app download required",
                "Works on any smartphone",
                "Instant maintenance records",
                "Export to Excel & PDF",
                "Secure PIN protection",
                "No IT setup needed"
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-[#22C55E] flex-shrink-0" />
                  <span className="text-[#475569]">{benefit}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="px-6 py-6 border-t border-[#E2E8F0] bg-white mt-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#475569] text-sm">
            © 2026 QuickQrId - Machine Maintenance QR System
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BuyPage;
